import { useState, createContext, useEffect, useContext } from "react";
import { onAuthStateChangedListener, getUserData } from "../utils/firebase/firebase.utils";


// The actual value you want to access
export const UserContext = createContext({
    currentUser: null,
    setCurrentUser: () => { },
    initialLoad: true,
    userError: false,
    setUserError: () => { },
    logOutMessage: '',
    setLogOutMessage: () => { },
    subscribeToEmails: false,
    setSubscribeToEmails: () => { },
    numFreeCredits: 0,
    freeCreditsUsed: 0,
    setFreeCreditsUsed: () => { },
    numCreditsAvailable: 0,
    setNumCreditsAvailable: () => { },
    userAuthStateIsChanging: false,
    setUserAuthStateIsChanging: () => { },
    planResetDay: null,
    planEnding: false,
    APIKeys: [],
    setAPIKeys: () => { },
    isLoadingUserData: true,
    APICreditsAvailable: 0,
    setAPICreditsAvailable: () => { },
    aliasSurveyIds: [],
    setAliasSurveyIds: () => { },
    aliasSurveysLoaded: false,
});

const getRemainingCredits = async (userId) => {
    const response = await fetch(`/.netlify/functions/getUserMetadata`, {
        method: "POST",
        body: JSON.stringify({ userId }),
    });
    return await response.json();
}

export const UserProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [logOutMessage, setLogOutMessage] = useState('');
    const [initialLoad, setInitialLoad] = useState(true);
    const [userError, setUserError] = useState(false);
    const [subscribeToEmails, setSubscribeToEmails] = useState(false);
    const [numFreeCredits, setNumFreeCredits] = useState(0);
    const [numFreeCreditsUsed, setNumFreeCreditsUsed] = useState(0);
    const [numCreditsAvailable, setNumCreditsAvailable] = useState(0);
    const [userAuthStateIsChanging, setUserAuthStateIsChanging] = useState(false);
    const [planResetDay, setPlanResetDay] = useState(null);
    const [planEnding, setPlanEnding] = useState(false);
    const [APIKeys, setAPIKeys] = useState([]);
    const [isLoadingUserData,setIsLoadingUserData] = useState(true);
    const [APICreditsAvailable,setAPICreditsAvailable] = useState(0);
    const [aliasSurveyIds,setAliasSurveyIds] = useState([]);
    const [aliasSurveysLoaded,setAliasSurveysLoaded] = useState(false);

    const value = {
        currentUser, setCurrentUser, initialLoad, logOutMessage, setLogOutMessage,
        userError, setUserError, subscribeToEmails, setSubscribeToEmails,
        numFreeCredits, numFreeCreditsUsed, setNumFreeCreditsUsed,
        numCreditsAvailable, setNumCreditsAvailable,
        userAuthStateIsChanging, setUserAuthStateIsChanging, planResetDay, planEnding,APIKeys,setAPIKeys,
        isLoadingUserData,APICreditsAvailable,setAPICreditsAvailable, aliasSurveyIds, setAliasSurveyIds, aliasSurveysLoaded
    };

    const fetchUserData = async (user) => {
        try {
            const { numFreeCredits, numFreeCreditsUsed, numCreditsAvailable, planResetDay,  planEnding, APIKeys, APICreditsAvailable, aliasSurveyIds } = await getRemainingCredits(user.uid);
            if (APIKeys) setAPIKeys(APIKeys);
            if (numFreeCredits) setNumFreeCredits(numFreeCredits);
            if (numFreeCreditsUsed) setNumFreeCreditsUsed(numFreeCreditsUsed);
            if (numCreditsAvailable) setNumCreditsAvailable(numCreditsAvailable);
            if (planResetDay) setPlanResetDay(planResetDay);
            if (planEnding) setPlanEnding(planEnding);
            if (APICreditsAvailable) setAPICreditsAvailable(APICreditsAvailable);
            if (aliasSurveyIds){
                const sortedAliasSurveyIds = aliasSurveyIds.sort();
                setAliasSurveyIds(sortedAliasSurveyIds);
            }
            setAliasSurveysLoaded(true);
            setIsLoadingUserData(false);
            const userData = await getUserData(user);
            if (userData) {
                // Redundant with set subscribe to emails in sign up, but sometimes this may be called before user doc is created.
                const subscribeToEmails = userData.subscribeToEmails === undefined ? false : userData.subscribeToEmails;
                setSubscribeToEmails(subscribeToEmails);
            }
        } catch (error) {
            console.log(error);
            setUserError(true);
        }
    }

    useEffect(() => {
        const unsubscribe = onAuthStateChangedListener((user) => {
            if (!userAuthStateIsChanging) {
                setCurrentUser(user);
                setInitialLoad(false);
                if (user) fetchUserData(user);
            }
        });
        return unsubscribe;
    }, [userAuthStateIsChanging]);

    return <UserContext.Provider value={value}>{!initialLoad && children}</UserContext.Provider>;
}

export const useUserAuth = () => {
    return useContext(UserContext);
} 